import { ReactNode } from 'react';
import { Caption1, Title2, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  DocumentBulletListRegular,
  ErrorCircleRegular,
  PresenceBlockedRegular,
  TaskListSquareLtrRegular,
} from '@fluentui/react-icons';

import { TDashboardSummary } from '../../utils';

export type TSummaryCardKey = 'myChanges' | 'myTasks' | 'overdue' | 'blocked';

const useStyles = makeStyles({
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 1100px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  cardActive: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: '20px',
    flexShrink: 0,
  },
  attention: {
    color: tokens.colorStatusDangerForeground1,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
});

type TProps = {
  summary: TDashboardSummary;
  activeKey?: TSummaryCardKey;
  onSelect: (key: TSummaryCardKey) => void;
};

export const DashboardSummaryCards = ({ summary, activeKey, onSelect }: TProps) => {
  const classes = useStyles();

  const cards: { key: TSummaryCardKey; label: string; count: number; icon: ReactNode; attention?: boolean }[] = [
    { key: 'myChanges', label: 'My changes', count: summary.myChanges, icon: <DocumentBulletListRegular /> },
    { key: 'myTasks', label: 'My tasks', count: summary.myTasks, icon: <TaskListSquareLtrRegular /> },
    { key: 'overdue', label: 'Overdue', count: summary.overdue, icon: <ErrorCircleRegular />, attention: true },
    { key: 'blocked', label: 'Blocked', count: summary.blocked, icon: <PresenceBlockedRegular />, attention: true },
  ];

  return (
    <div className={classes.row}>
      {cards.map((card) => (
        <button
          key={card.key}
          type="button"
          className={mergeClasses(classes.card, activeKey === card.key && classes.cardActive)}
          onClick={() => onSelect(card.key)}
          aria-pressed={activeKey === card.key}
        >
          <span className={mergeClasses(classes.icon, card.attention && card.count > 0 && classes.attention)}>
            {card.icon}
          </span>
          <span className={classes.body}>
            <Title2>{card.count}</Title2>
            <Caption1 className={classes.label}>{card.label}</Caption1>
          </span>
        </button>
      ))}
    </div>
  );
};
